import {
  type KeyboardEvent,
  type Ref,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { twJoin } from "tailwind-merge";

import { Input } from "~/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";

interface SelectComboboxProps {
  options: string[];
  value: string | null;
  onChange: (value: string | null) => void;
  className?: string;
  ref?: Ref<HTMLInputElement>;
}

export function SelectCombobox({
  options,
  value,
  onChange,
  className,
  ref,
}: SelectComboboxProps) {
  const id = useId();
  const [hoveredIndex, setHoveredIndex] = useState<number>(0);
  const [listboxHover, setListboxHover] = useState(false);
  const previousValue = useRef(value);
  const visibleOptions =
    value && !options.includes(value)
      ? options.filter((option) => option.startsWith(value))
      : options;
  const [isExpanded, setIsExpanded] = useState(false);
  const listboxRef = useRef<HTMLDivElement>(null);

  const scrollIntoView = useCallback((index: number) => {
    listboxRef.current?.children[index]?.scrollIntoView({
      block: "center",
    });
  }, []);

  useEffect(() => {
    if (visibleOptions.length === 0) {
      setIsExpanded(false);
    }
    if (visibleOptions.length <= hoveredIndex) {
      setHoveredIndex(0);
    }
  }, [visibleOptions.length, hoveredIndex]);

  useEffect(() => {
    if (!isExpanded) {
      return;
    }
    setHoveredIndex(
      options.findIndex((option) => option.startsWith(value ?? ""))
    );
    scrollIntoView(hoveredIndex);
  }, [
    value,
    isExpanded,
    hoveredIndex,
    options.findIndex,
    scrollIntoView,
    options,
  ]);

  const onInputKeyDown = (e: KeyboardEvent) => {
    if (!(visibleOptions.length && isExpanded)) {
      return;
    }
    switch (e.key) {
      case "Enter":
      case "Space": {
        e.preventDefault();
        const selectedOption = visibleOptions[hoveredIndex];
        if (selectedOption) {
          onChange(selectedOption);
        }
        setIsExpanded(false);
        break;
      }
      case "Escape":
        e.preventDefault();
        setIsExpanded(false);
        break;
      case "ArrowDown":
        e.preventDefault();
        setHoveredIndex((i) => (i + 1) % visibleOptions.length);
        scrollIntoView(hoveredIndex);
        break;
      case "ArrowUp":
        e.preventDefault();
        setHoveredIndex((i) => (i - 1 < 0 ? visibleOptions.length - 1 : i - 1));
        scrollIntoView(hoveredIndex);
        break;
      case "Home":
        e.preventDefault();
        setHoveredIndex(0);
        break;
      case "End":
        e.preventDefault();
        setHoveredIndex(visibleOptions.length - 1);
        break;
      default:
        break;
    }
  };

  return (
    <Popover open={isExpanded}>
      <PopoverTrigger asChild>
        <Input
          aria-activedescendant={
            isExpanded ? `${id}-${hoveredIndex.toString()}` : undefined
          }
          aria-autocomplete="list"
          aria-controls={isExpanded ? `${id}-listbox` : undefined}
          aria-expanded={isExpanded}
          className={className}
          onBlur={() => {
            if (listboxHover) {
              return;
            }

            setIsExpanded(false);

            if (!value) {
              return onChange(previousValue.current);
            }

            if (value && !options.includes(value)) {
              const defaultOptionByValue = options.find((option) =>
                option.includes(value)
              );

              return defaultOptionByValue
                ? onChange(defaultOptionByValue)
                : onChange(previousValue.current);
            }
          }}
          onChange={(e) => {
            if (value) {
              previousValue.current = value;
            }
            const newOptions = options.filter((option) =>
              option.startsWith(e.currentTarget.value)
            );
            if (value && e.currentTarget.value < value) {
              onChange(e.currentTarget.value);
              if (newOptions.length > 0) {
                setIsExpanded(true);
              }
              return;
            }
            if (newOptions.length === 0 || !newOptions[0]) {
              return;
            }
            if (newOptions.length === 1) {
              onChange(newOptions[0]);
              setIsExpanded(false);
              return;
            }
            let newValue = e.currentTarget.value;
            for (let i = newValue.length + 1; i < newOptions[0].length; i++) {
              const possibleNewVal = newOptions[0].slice(0, i);
              if (
                newOptions.every((option) => option.startsWith(possibleNewVal))
              ) {
                newValue = possibleNewVal;
              } else {
                break;
              }
            }
            setIsExpanded(true);
            onChange(newValue);
          }}
          onFocus={() => {
            if (visibleOptions?.length) {
              setIsExpanded(true);
            }
            setTimeout(() => {
              scrollIntoView(visibleOptions.indexOf(value ?? ""));
            }, 0);
          }}
          onKeyDown={onInputKeyDown}
          ref={ref}
          role="combobox"
          value={value ?? ""}
        />
      </PopoverTrigger>
      <PopoverContent align="start" className="z-50 p-0" sideOffset={4}>
        <div
          aria-orientation="vertical"
          className="scrollbar-thumb-muted scrollbar-thumb-rounded-full border-border bg-background shadow-box max-h-40 scrollbar-thin scrollbar-track-transparent overflow-x-hidden overflow-y-auto rounded-md border p-2"
          id={`${id}-listbox`}
          ref={listboxRef}
          role="listbox"
        >
          {visibleOptions.map((option, index) => (
            <div
              aria-selected={value === option}
              className={twJoin(
                "cursor-pointer select-none items-center rounded-md px-3 py-2",
                hoveredIndex === index
                  ? "bg-muted text-foreground"
                  : "text-foreground"
              )}
              id={`${id}-${index.toString()}`}
              key={option}
              onClick={() => {
                onChange(option);
                setListboxHover(false);
                setIsExpanded(false);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onChange(option);
                  setListboxHover(false);
                  setIsExpanded(false);
                }
              }}
              onMouseEnter={() => {
                setHoveredIndex(index);
                setListboxHover(true);
              }}
              onMouseLeave={() => setListboxHover(false)}
              role="option"
              tabIndex={0}
            >
              {option}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
